import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { d, dialerError } from "../../lib/dialer";
import useModalFoco from "../../hooks/useModalFoco";
import { fmtTel, nuevoId, errorNota, normalizePhone, recordarIntento } from "../../lib/notas";
import { emitirTraspaso } from "../../lib/traspasoLlamada";
import LogoCuenta from "./LogoCuenta";
import LogosNumero from "../LogosNumero";

export const VENTANA_USCALLER = "usecaller-commandix";   // nombre fijo: así se reutiliza SIEMPRE la misma pestaña de USCaller
/** Enlace que abre USCaller con el número (10 dígitos, sin +1) en la casilla del teléfono; lo recoge la extensión de Chrome de tools/usecaller-chrome-extension. */
export const urlUsecaller = (phone) => `https://usecaller.com/dialer#cx_num=${String(phone).replace(/^\+1/, "")}`;
/** Abre la app de escritorio registrada para tel: (Ooma Office) con el número puesto, sin salir de esta página. */
export function abrirTel(phone) {
  const a = document.createElement("a");
  a.href = `tel:${phone}`; a.style.display = "none";
  document.body.appendChild(a); a.click(); a.remove();
}

/**
 * Segundo plano (la ventanita ya se cerró): registra el intento (todavía NO cuenta como llamada), deja el número en espera para el teléfono
 * y emite el traspaso al Centro de Llamadas: si está abierto en otra pestaña lo recoge al instante; si no, queda guardado y lo recoge al abrirse. Solo los ERRORES se avisan (toast); el éxito es silencioso.
 */
export async function registrarIntento({ phone, noteId, cuenta }) {
  try {
    const r = await d.post("/attempts", { attempt_id: nuevoId("n"), phone, account_id: cuenta.id, ...(noteId ? { note_id: noteId } : {}) });
    const intento = r.data?.attempt || {};
    if (intento.id) recordarIntento(phone, intento.id);   // para el toque de resultado («no contestó»/«buzón»/...) junto al número
    // Precarga el número en el puesto SOLO si hay uno claro (un solo puesto): con varios no se adivina cuál.
    try {
      const st = await d.get("/stations");
      if (Array.isArray(st.data) && st.data.length === 1) await d.post("/manual/preload", { station_id: st.data[0].id, phone });
    } catch (e) { /* la precarga del servidor es de apoyo: el teléfono igual recibe el número desde esta entrega */ }
    emitirTraspaso({ phone, attempt_id: intento.id || null, cuenta: cuenta.name, app: cuenta.app, counter: r.data?.counter || null });
    if (cuenta.app === "uscaller") avisarRelevo(r.data?.relay, cuenta);
  } catch (e) {
    toast.error(e?.response?.data?.detail?.code === "account_unavailable" ? "Esa cuenta ya no está disponible. Elige otra." : (dialerError(e) || errorNota(e)));
  }
}

/** Aviso pequeño y no bloqueante: «Enviado a USCaller 2». */
export const textoEnviado = (cuenta) => `Enviado a ${cuenta.name}`;

/** El número SIEMPRE va por el relevo al Chrome de la cuenta; aquí no se abre nada ni se pide nada. Solo si el servidor no pudo encolarlo se dice (sin opciones). */
function avisarRelevo(relay, cuenta) {
  const st = relay?.status;
  if (st === "queued" || st === "delivered") toast.success(textoEnviado(cuenta), { duration: 3000 });
  else toast.error(`No se pudo enviar el número a ${cuenta.name}. Vuelve a intentarlo.`);
}

/**
 * «¿Por dónde llamar?»: un clic en un número abre esto. Elegir una cuenta CIERRA la ventana de inmediato (sin aviso ni espera, sin ir a ninguna parte):
 * en el mismo clic se abre la app de Ooma (tel:) o la pestaña de USCaller con el número, y en segundo plano se registra el intento.
 */
export default function PopupCuentas({ phone, noteId, onCerrar }) {
  const [cuentas, setCuentas] = useState(null);
  const [contador, setContador] = useState(null);
  const [error, setError] = useState("");
  const trabajando = useRef(false);
  const dialogoRef = useRef(null);
  useModalFoco(dialogoRef, onCerrar);

  const cargar = async () => {
    try { const r = await d.get("/call-accounts"); setCuentas(r.data?.items || []); setError(""); }
    catch (e) { setCuentas([]); setError(dialerError(e)); }
  };
  useEffect(() => {
    let vivo = true;
    cargar();
    d.get("/attempts/counters", { params: { phones: phone } }).then((r) => { if (vivo) setContador((r.data?.items || [])[0] || null); }).catch(() => {});
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onCerrar?.(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onCerrar]);

  const elegir = (cuenta) => {
    if (trabajando.current || !cuenta.enabled) return;
    const norm = normalizePhone(phone);
    if (!norm) { trabajando.current = true; toast.error("Ese número no es válido."); onCerrar?.(); return; }
    trabajando.current = true;
    // Todo lo que abre algo por fuera va AQUÍ, en el mismo gesto del clic (si no, el navegador lo bloquea).
    // USCaller: NO se abre nada aquí; el servidor manda SIEMPRE el número al Chrome de esa cuenta y el aviso sale al confirmarse.
    if (cuenta.app === "ooma") abrirTel(norm);
    onCerrar?.();
    registrarIntento({ phone: norm, noteId, cuenta });   // sin await: no bloquea nada
  };

  const c = contador || null;
  // la cuenta usada más recientemente (por app+slot) lleva la etiqueta ÚLTIMA
  const ultima = (c?.by_account || []).reduce((best, x) => (!best || (x.last_at && (!best.last_at || x.last_at > best.last_at)) ? x : best), null);
  const esUltima = (a) => !!ultima?.last_at && ultima.app === a.app && (ultima.slot ?? null) === (a.slot ?? null);
  return (
    <div className="nt-overlay" data-testid="nt-popup-fondo" onMouseDown={(e) => { if (e.target === e.currentTarget) onCerrar?.(); }}>
      <div className="nt-pp" role="dialog" aria-modal="true" aria-label="¿Por dónde llamar?" data-testid="nt-popup" ref={dialogoRef}>
        <div className="nt-row">
          <div><div className="nt-hint">LLAMAR A</div><div className="nt-nm" data-testid="nt-popup-numero">{fmtTel(phone)}<LogosNumero phone={normalizePhone(phone)} /></div></div>
          <span className="nt-sp" />
          <button type="button" className="nt-pill" aria-label="Cerrar" data-testid="nt-popup-cerrar" onClick={onCerrar}>✕</button>
        </div>
        {c && c.count > 0 && <div className="nt-pill am" data-testid="nt-popup-contador">Llamado {c.count} {c.count === 1 ? "vez" : "veces"} en {c.window_days || 30} días</div>}
        {cuentas === null && <div className="nt-hint">Cargando cuentas…</div>}
        {cuentas && !cuentas.length && !error && <div className="nt-aviso" data-testid="nt-popup-sin-cuentas">No hay cuentas de llamadas. Pídele a tu gerente que las agregue en Configuración.</div>}
        {(cuentas || []).map((a) => (
          <button type="button" key={a.id} className={`nt-opt${a.enabled ? "" : " off"}`} disabled={!a.enabled} data-testid="nt-cuenta" data-enabled={a.enabled ? "1" : "0"} onClick={() => elegir(a)}>
            <LogoCuenta cuenta={a} />
            <span className="nt-opt-tx"><b>{a.name}</b><small>{!a.enabled ? "Apagada" : a.app === "ooma" ? "Se abre la app de Ooma con el número" : a.chrome_profile ? `Se envía a su Chrome «${a.chrome_profile}»` : "Se envía al Chrome de esta cuenta"}</small></span>
            {esUltima(a) ? <span className="nt-pill am" data-testid="nt-popup-ultima">ÚLTIMA</span> : a.enabled ? <span className="nt-go">›</span> : <span className="nt-pill am">APAGADA</span>}
          </button>
        ))}
        {error && <div className="nt-aviso" role="alert" data-testid="nt-popup-error">{error}</div>}
        {cuentas !== null && (
          <button type="button" className="nt-opt off" disabled data-testid="nt-opcion-commandix" data-enabled="0">
            <span className="nt-opt-tx"><b>Teléfono Commandix</b><small>La ruta propia de Commandix</small></span>
            <span className="nt-pill am">Próximamente</span>
          </button>
        )}
        <div className="nt-hint">Un clic solo <b>prepara</b> la llamada. Se cuenta cuando la llamada se registra y se cierra.</div>
      </div>
    </div>
  );
}
